import React, { useState, useEffect, useCallback } from "react";
import { ReviewsContainerProps } from "../../types/ReviewTypes";
import { Review } from "../../types/ReviewTypes";
import ReviewItem from "./ReviewItem";
import ReviewFormInline from "./ReviewFormInline";
import { authFetch, isLoggedIn } from "../../utils/authUtils";
import "./Review.css";

const ReviewsContainer: React.FC<ReviewsContainerProps> = ({ songId }) => {
    const [reviews, setReviews] = useState<Review[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingReview, setEditingReview] = useState<Review | null>(null);
    const [userHasReview, setUserHasReview] = useState(false);

    const fetchReviews = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await authFetch(`/api/reviews/song/${songId}`);

            if (!response.ok) {
                throw new Error(`Error: ${response.status}`);
            }

            const data = await response.json();
            setReviews(data);

            // Check if the current user has already reviewed this song
            setUserHasReview(data.some((review: Review) => review.is_owner));
        } catch (err) {
            console.error("Error fetching reviews:", err);
            setError("Failed to load reviews");
        } finally {
            setIsLoading(false);
        }
    }, [songId]);

    useEffect(() => {
        fetchReviews();
    }, [songId, fetchReviews]);

    useEffect(() => {
        const handleReviewsListUpdate = (event: Event) => {
            // Check if this event is for our song
            const customEvent = event as CustomEvent;
            if (customEvent.detail?.songId === songId) {
                console.log("ReviewsContainer received update event, refreshing reviews");
                fetchReviews();
            }
        };

        // Add event listener
        window.addEventListener('reviewsListUpdated', handleReviewsListUpdate);
        window.addEventListener('reviewUpdated', fetchReviews);

        // Clean up
        return () => {
            window.removeEventListener('reviewsListUpdated', handleReviewsListUpdate);
            window.removeEventListener('reviewUpdated', fetchReviews);
        };
    }, [songId, fetchReviews]);

    const handleEditReview = (review: Review) => {
        setEditingReview(review);
    };

    const handleReviewAdded = (newReview: Review) => {
        setReviews(prevReviews => [newReview, ...prevReviews]);
        setUserHasReview(true);
    };

    const handleReviewUpdated = (updatedReview: Review) => {
        setReviews(prevReviews =>
            prevReviews.map(review =>
                review.id === updatedReview.id ? updatedReview : review
            )
        );
        setEditingReview(null);
    };

    const handleCancelEdit = () => {
        setEditingReview(null);
    };

    return (
        <div className="reviews-container">
            <div className="reviews-header">
                <div className="top-line-review-header">
                    <p style={{ "fontSize": "1.1em", "fontWeight": "600", "marginTop": "2rem", "width": "100%" }}>
                        REVIEWS </p>
                </div>
                <hr></hr>

                {/* Show inline review form by default if user is logged in and doesn't have a review */}
                {isLoggedIn() && !userHasReview && (
                    <div className="review-form-section">
                        <ReviewFormInline
                            songId={songId}
                            existingReview={null}
                            onReviewAdded={handleReviewAdded}
                            onReviewUpdated={handleReviewUpdated}
                            onCancel={() => { }} // No cancel action needed since form is always visible
                        />
                    </div>
                )}

                {/* Show edit form if user is editing their review */}
                {editingReview && (
                    <div className="review-form-section">
                        <ReviewFormInline
                            songId={songId}
                            existingReview={editingReview}
                            onReviewAdded={handleReviewAdded}
                            onReviewUpdated={handleReviewUpdated}
                            onCancel={handleCancelEdit}
                        />
                    </div>
                )}

                {isLoggedIn() && userHasReview && !editingReview && (
                    <div className="review-status">You've already reviewed this song.</div>
                )}

                {!isLoggedIn() && (
                    <div className="login-prompt">
                        <a href="/login">Log in</a> to write a review.
                    </div>
                )}

            </div>

            {isLoading ? (
                <div className="loading-reviews">Loading reviews...</div>
            ) : error ? (
                <div className="error-message">{error}</div>
            ) : reviews.length === 0 ? (
                <div className="no-reviews">
                    No reviews yet. Be the first to review this song!
                </div>
            ) : (
                <div className="reviews-list">
                    {reviews
                        .filter(review => editingReview ? review.id !== editingReview.id : true)
                        .map(review => (
                            <ReviewItem
                                key={review.id}
                                review={review}
                                onEditReview={handleEditReview}
                            />
                        ))}
                </div>
            )}
        </div>
    );
};

export default ReviewsContainer; 